import { Camera, Check, RotateCcw, SwitchCamera } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from './button.js';
import {
  countCameras,
  openCamera,
  photoFromVideo,
  stopCamera,
  type CameraFacing,
} from './camera.js';
import { cn } from './cn.js';
import { Modal } from './modal.js';

/// Fotografar um papel sem sair do sistema.
///
/// A alternativa barata seria `capture="environment"` no campo de arquivo, que entrega a pessoa ao
/// aplicativo de câmera do sistema. Ela foi descartada por duas razões: no notebook o atributo é
/// **ignorado** — e o pedido era câmera do celular *ou* do notebook —, e sair para outro aplicativo
/// e voltar é justamente o tipo de saída do sistema que a interface daqui não faz.
///
/// A janela tem três momentos: a prévia ao vivo, a foto tirada e a decisão de usar ou repetir.
/// Repetir existe porque a primeira foto de um papel quase nunca presta.
export function CameraCapture({
  open,
  onOpenChange,
  onCapture,
  fileName = 'foto.jpg',
  title = 'Tirar foto',
  description = 'Enquadre o documento e toque em Fotografar.',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCapture: (file: File) => void;
  /// Como a foto se chama na lista até subir. Quem chama sabe quantas já existem; a janela, não.
  fileName?: string | undefined;
  title?: string | undefined;
  description?: string | undefined;
}) {
  /// A lente escolhida mora aqui fora porque trocá-la **remonta** o corpo: assim ligar a câmera é
  /// sempre o mesmo caminho, do zero, em vez de um estado que precisa ser desfeito no meio.
  const [facing, setFacing] = useState<CameraFacing>('environment');

  return (
    <Modal open={open} onOpenChange={onOpenChange} title={title} description={description}>
      {/* O corpo só existe enquanto a janela está aberta. É o que apaga a foto, devolve o endereço
          do objeto e desliga a câmera ao fechar — sem nenhum efeito encarregado de desfazer. */}
      {open ? (
        <CameraBody
          key={facing}
          facing={facing}
          fileName={fileName}
          onFlip={() =>
            setFacing((current) => (current === 'environment' ? 'user' : 'environment'))
          }
          onCapture={(file) => {
            onCapture(file);
            onOpenChange(false);
          }}
        />
      ) : null}
    </Modal>
  );
}

function CameraBody({
  facing,
  fileName,
  onFlip,
  onCapture,
}: {
  facing: CameraFacing;
  fileName: string;
  onFlip: () => void;
  onCapture: (file: File) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  /// O endereço do objeto precisa ser devolvido mesmo quando a janela some de repente, e nesse
  /// instante o estado já não é legível — daí a cópia numa referência.
  const photoUrlRef = useRef<string | null>(null);
  const [cameras, setCameras] = useState(0);
  const [starting, setStarting] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [photo, setPhoto] = useState<{ file: File; url: string } | null>(null);

  /// Liga a câmera ao montar e a desliga ao sair. A bandeira `active` existe porque abrir a câmera
  /// demora o tempo da permissão: fechar antes da resposta deixaria uma faixa de vídeo ligada sem
  /// ninguém para desligá-la — que é como se esquece a luz da lente acesa.
  useEffect(() => {
    let active = true;
    let opened: MediaStream | null = null;

    void (async () => {
      const result = await openCamera(facing);
      if (!active) {
        if (result.ok) stopCamera(result.stream);
        return;
      }
      if (!result.ok) {
        setError(result.detail);
        setStarting(false);
        return;
      }
      opened = result.stream;
      if (videoRef.current) videoRef.current.srcObject = result.stream;
      setStarting(false);
      const found = await countCameras();
      if (active) setCameras(found);
    })();

    return () => {
      active = false;
      stopCamera(opened);
      if (photoUrlRef.current) URL.revokeObjectURL(photoUrlRef.current);
    };
  }, [facing]);

  async function capture() {
    const video = videoRef.current;
    if (!video) return;
    setBusy(true);
    const file = await photoFromVideo(video, fileName);
    setBusy(false);
    if (!file) {
      setError('Não foi possível capturar a foto. Tente de novo.');
      return;
    }
    const url = URL.createObjectURL(file);
    photoUrlRef.current = url;
    setError(undefined);
    setPhoto({ file, url });
  }

  function discard() {
    if (photoUrlRef.current) URL.revokeObjectURL(photoUrlRef.current);
    photoUrlRef.current = null;
    setPhoto(null);
    setError(undefined);
  }

  function use() {
    if (!photo) return;
    /// O arquivo sai antes de o endereço ser devolvido: quem recebe guarda o `File`, e o endereço
    /// só servia à prévia daqui.
    onCapture(photo.file);
    URL.revokeObjectURL(photo.url);
    photoUrlRef.current = null;
  }

  return (
    <>
      <div className="relative aspect-[4/3] overflow-hidden rounded-xl border border-surface-border bg-black">
        {/* O vídeo nunca sai da árvore, nem enquanto a foto está em revisão: tirá-lo faria a faixa
            da câmera se soltar do elemento, e "repetir" voltaria para uma prévia preta. */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          aria-label="Prévia da câmera"
          className={cn('size-full object-contain', photo ? 'invisible' : null)}
        />
        {photo ? (
          <img
            src={photo.url}
            alt="Foto tirada agora"
            className="absolute inset-0 size-full object-contain"
          />
        ) : null}
        {starting && !error ? (
          <p className="absolute inset-0 grid place-items-center px-6 text-center text-sm text-white/70">
            Abrindo a câmera…
          </p>
        ) : null}
        {error ? (
          <p
            role="alert"
            className="absolute inset-0 grid place-items-center px-6 text-center text-sm text-white/80"
          >
            {error}
          </p>
        ) : null}
      </div>

      <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        {photo ? (
          <>
            <Button className="w-full sm:w-auto" type="button" variant="outline" onClick={discard}>
              <RotateCcw className="size-4" />
              Repetir
            </Button>
            <Button className="w-full sm:w-auto" type="button" onClick={use}>
              <Check className="size-4" />
              Usar foto
            </Button>
          </>
        ) : (
          <>
            {/* Só aparece onde há para onde virar. Num notebook de uma câmera só, o botão seria
                uma promessa que gira em falso. */}
            {cameras > 1 ? (
              <Button className="w-full sm:w-auto" type="button" variant="outline" onClick={onFlip}>
                <SwitchCamera className="size-4" />
                Virar a câmera
              </Button>
            ) : null}
            <Button
              className="w-full sm:w-auto"
              type="button"
              loading={busy}
              disabled={starting || Boolean(error)}
              onClick={() => void capture()}
            >
              {busy ? null : <Camera className="size-4" />}
              Fotografar
            </Button>
          </>
        )}
      </div>
    </>
  );
}
